import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { TitleIcon } from "components/icons/TitleIcon";
import FailedView from "components/import-file-dialog/FailedView";
import style from "components/import-file-dialog/import-file-dialog.scss";
import ImportFileDialog, { DialogState } from "components/import-file-dialog/ImportFileDialog";
import LoadingView from "components/import-file-dialog/LoadingView";
import SelectingView from "components/import-file-dialog/SelectingView";
import SucceededView from "components/import-file-dialog/SucceededView";
import { isWorkflow, Workflow, WorkflowEntityType } from "domain/workflows";
import { ImportReportView } from "services/report/ReportViewService";
import { TemplateService } from "services/workflows/TemplateService";
import { WorkflowService } from "services/workflows/WorkflowService";
import { StoreState } from "store";

interface Props {
    onClose: (reportViewValues?: ImportReportView) => void;
    service?: WorkflowService | TemplateService;
    fileList?: File[];
    setModalTitle: (title: string) => void;
    setModalTitleIcon: (icon: JSX.Element | undefined) => void;
    entityType?: WorkflowEntityType;
}

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

interface SaveError {
    field: string;
    message: string;
}

const ERROR_TO_TRANSLATION: Record<string, Record<string, string>> = {
    name: {
        "already exists": "ImportWorkflowDialog.importWorkflow.error.nameTakenMessage",
        "contains illegal characters": "ImportWorkflowDialog.importWorkflow.error.nameIllegalMessage",
        "must not be blank": "ImportWorkflowDialog.importWorkflow.error.nameMissingMessage",
        "size must be between": "ImportWorkflowDialog.importWorkflow.error.nameLengthMessage",
    },
    description: {
        "size must be between": "ImportWorkflowDialog.importWorkflow.error.descriptionLengthMessage",
    },
};

const ImportWorkflowsDialog = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [dialogState, setDialogState] = React.useState(DialogState.SELECTING_FILE);
    const [error, setError] = React.useState<SaveError | string | undefined>(undefined);
    const [workflow, setWorkflow] = React.useState<Workflow | undefined>(undefined);
    const [missingSubWorkflows, setMissingSubWorkflows] = React.useState<string[]>([]);
    const selectedFileListReference = React.useRef<File[] | undefined>(props.fileList);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const MC_ERROR_FORBIDDEN = "MC_ERROR_FORBIDDEN";

    const triggerGenericError = () => {
        setError({ field: "workflow", message: "" });
        props.setModalTitleIcon(<TitleIcon iconType={"ERROR"} />);
        setDialogState(DialogState.IMPORTING_FAILED);
    };

    const dialogLogic = () => {
        switch (dialogState) {
            case DialogState.SELECTING_FILE:
                setError(undefined);
                return;

            case DialogState.LOADING_FILE: {
                if (selectedFileListReference.current === undefined) {
                    setError(t("Common.noFileSelected"));
                    return;
                }

                const reader = new FileReader();
                reader.readAsText(selectedFileListReference.current[0]);
                reader.onload = () => {
                    try {
                        if (typeof reader.result === "string") {
                            const candidate = JSON.parse(reader.result);
                            if (isWorkflow(candidate)) {
                                setWorkflow(candidate);
                                setDialogState(DialogState.IMPORTING_WORKFLOW);
                            } else {
                                triggerGenericError();
                            }
                        }
                    } catch {
                        triggerGenericError();
                    }
                };
                return;
            }
            case DialogState.IMPORTING_WORKFLOW: {
                if (workflow === undefined || props.service === undefined) {
                    setError(t("ImportWorkflowDialog.noWorkflowAvailable"));
                    return;
                }
                const abortController = new AbortController();
                abortControllers.push(abortController);
                props.service
                    .create(
                        workflow,
                        props.entityType === WorkflowEntityType.SUB_WORKFLOW ? true : undefined,
                        abortController
                    )
                    .then((response) => {
                        props.setModalTitle(
                            props.entityType == WorkflowEntityType.TEMPLATE
                                ? t("workflowTemplatesManager.ImportWorkflowTemplateDialog.title")
                                : props.entityType == WorkflowEntityType.SUB_WORKFLOW
                                  ? t("workflowTemplatesManager.ImportSubWorkflowTemplateDialog.title")
                                  : t("ImportWorkflowDialog.titleUploaded")
                        );
                        if (response.missingSubWorkflows.length > 0) {
                            setMissingSubWorkflows(response.missingSubWorkflows);
                            props.setModalTitleIcon(<TitleIcon iconType="WARNING" />);
                            setDialogState(DialogState.IMPORTING_SUCCEEDED_MISSING_SUB_WORKFLOWS);
                            return;
                        } else {
                            props.setModalTitleIcon(<TitleIcon iconType={"SUCCESS"} />);
                            setDialogState(DialogState.IMPORTING_SUCCEEDED);
                        }
                    })
                    .catch((error) => {
                        props.setModalTitle(
                            props.entityType == WorkflowEntityType.TEMPLATE
                                ? t("workflowTemplatesManager.ImportWorkflowTemplateDialog.titleUploadFailed")
                                : props.entityType == WorkflowEntityType.SUB_WORKFLOW
                                  ? t("workflowTemplatesManager.ImportSubWorkflowTemplateDialog.titleUploadFailed")
                                  : t("ImportWorkflowDialog.titleUploadFailed")
                        );
                        try {
                            const response = JSON.parse(error.message);
                            if (response.error?.code === MC_ERROR_FORBIDDEN) {
                                setError({
                                    field: "license",
                                    message: t("ImportWorkflowDialog.importWorkflow.error.insufficientLicenseMessage"),
                                });
                            } else {
                                setError({ field: response.fieldName, message: response.errorMessage });
                            }
                        } catch {
                            setError({
                                field: "workflow",
                                message: error.message || t("ImportWorkflowDialog.importWorkflow.error.genericMessage"),
                            });
                        }
                        props.setModalTitleIcon(<TitleIcon iconType={"ERROR"} />);
                        setDialogState(DialogState.IMPORTING_FAILED);
                    });
                return;
            }
        }
    };

    const dispatch = () => {
        if (selectedFileListReference.current === undefined || selectedFileListReference.current?.length === 0) {
            setDialogState(DialogState.SELECTING_FILE);
        } else if (selectedFileListReference.current?.length === 1) {
            setDialogState(DialogState.LOADING_FILE);
        } else {
            setDialogState(DialogState.LOADING_FILES);
        }
    };

    const handleFileDrop = (fileList: FileList, event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (fileList.length > 0) {
            selectedFileListReference.current = Array.from(fileList);
        }
        dispatch();
    };

    const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files !== null) {
            selectedFileListReference.current = Array.from(files);
        }
        dispatch();
    };

    const onSelectFileClicked = () => {
        fileInputRef.current?.click();
    };

    const reset = () => {
        selectedFileListReference.current = undefined;
        props.setModalTitle(
            props.entityType == WorkflowEntityType.TEMPLATE
                ? t("workflowTemplatesManager.ImportWorkflowTemplateDialog.title")
                : props.entityType == WorkflowEntityType.SUB_WORKFLOW
                  ? t("workflowTemplatesManager.ImportSubWorkflowTemplateDialog.title")
                  : t("ImportWorkflowDialog.titleUploaded")
        );
        props.setModalTitleIcon(undefined);
        setMissingSubWorkflows([]);
        setWorkflow(undefined);
        setError(undefined);
        setDialogState(DialogState.SELECTING_FILE);
    };

    const selecting = (
        <SelectingView
            introductionMessage={
                props.entityType === WorkflowEntityType.WORKFLOW
                    ? t("ImportWorkflowDialog.selectFile.introductionLabel")
                    : ""
            }
            handleFileDrop={handleFileDrop}
            onFileInputChange={onFileInputChange}
            onSelectFileClicked={onSelectFileClicked}
            fileInputRef={fileInputRef}
        />
    );
    const loadingFile = <LoadingView loadingMessage={t("Common.uploading")} />;
    const loadingFiles = (
        <FailedView
            failureMessage={t("ImportWorkflowDialog.loadFiles.notSupported")}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );
    const loadingFailed = (
        <FailedView
            failureMessage={typeof error === "string" ? t(error) : t("Common.failedToLoadFile")}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );
    const importing = <LoadingView loadingMessage={t("ImportWorkflowDialog.importWorkflow.loadingMessage")} />;
    const successElement = (
        <div className={style.introductionLabel}>
            <strong>{workflow?.name}</strong> {t("ImportWorkflowDialog.importWorkflow.successMessage")}
        </div>
    );
    const importingSucceeded = <SucceededView message={successElement} reset={reset} onClose={props.onClose} />;

    const warningContent = (
        <div>
            <ul>
                {missingSubWorkflows.map((name) => (
                    <li key={name}>{name}</li>
                ))}
            </ul>
        </div>
    );
    const importingSucceededMissingSubWorkflows = (
        <SucceededView
            message={t("ImportWorkflowDialog.importWorkflow.missingSubWorkflowsWarning", {
                name: workflow?.name,
            })}
            reset={reset}
            onClose={props.onClose}
            bodyContent={warningContent}
        />
    );
    const importingFailedErrorMessage = (
        <div className={style.introductionLabel}>
            {errorResponseToMessageElement(workflow?.name, selectedFileListReference.current?.[0]?.name, error, t)}
        </div>
    );

    const importingFailed = (
        <FailedView
            failureMessage={importingFailedErrorMessage}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );

    const stateToContent = new Map<DialogState, JSX.Element>([
        [DialogState.SELECTING_FILE, selecting],
        [DialogState.LOADING_FILE, loadingFile],
        [DialogState.LOADING_FILES, loadingFiles],
        [DialogState.LOADING_FILE_FAILED, loadingFailed],
        [DialogState.IMPORTING_WORKFLOW, importing],
        [DialogState.IMPORTING_SUCCEEDED, importingSucceeded],
        [DialogState.IMPORTING_SUCCEEDED_MISSING_SUB_WORKFLOWS, importingSucceededMissingSubWorkflows],
        [DialogState.IMPORTING_FAILED, importingFailed],
    ]);
    return (
        <ImportFileDialog
            fileList={props.fileList}
            onClose={props.onClose}
            service={props.service}
            dialogLogic={dialogLogic}
            stateToContent={stateToContent}
            stateDispatch={dispatch}
            dialogState={dialogState}
        />
    );
};

export function errorResponseToMessageElement(
    workflowName: string | undefined,
    fileName: string | undefined,
    error: SaveError | string | undefined,
    t: TFunction
) {
    const displayName = workflowName || fileName;

    if (error === undefined || typeof error === "string") {
        return <>{t("ImportWorkflowDialog.importWorkflow.error.genericMessage")}</>;
    }

    if (error.field === "license") {
        return <>{error.message}</>;
    }

    if (error.field === "workflow" || error.field === "profile" || error.field === "itemType") {
        return (
            <>
                <strong>{`"${displayName}"`}</strong>{" "}
                {t("ImportWorkflowDialog.importWorkflow.error.genericJsonMessage")}
            </>
        );
    }
    const fieldErrors = ERROR_TO_TRANSLATION[error.field];
    if (fieldErrors) {
        const messageKey = Object.keys(fieldErrors).find((key) => error.message.includes(key));
        if (messageKey) {
            return (
                <>
                    <strong>{`"${displayName}"`}</strong> {t(fieldErrors[messageKey])}
                </>
            );
        }
    }
    return (
        <>
            <strong>{`"${displayName}"`}</strong> {t("ImportWorkflowDialog.importWorkflow.error.genericMessage")}
        </>
    );
}

export default connector(ImportWorkflowsDialog);
